import type { TLlmTechnicalDetails } from './types';

export const mockVulnerabilityTechnicalDetails: TLlmTechnicalDetails = {
  summary:
    "The vulnerability allows an attacker to steal sensitive data from the Android device by exploiting the WebView's ability to load arbitrary URLs. The attacker crafts a malicious URL, loads it into the WebView, and then retrieves the content of a file on the device.",
  attackVector: 'Network',
  attackLocality: 'Remote',
  privilegesRequired: 'None',
  userInteraction: 'Required',
  scope: 'Changed',
  confidentialityImpact: 'High',
  integrityImpact: 'Low',
  availabilityImpact: 'None',
};

export const mockBusinessImpact =
  'This vulnerability allows a malicious app on the same device to silently steal sensitive files ' +
  'belonging to your application, including stored user credentials, session tokens, and private ' +
  'configuration data. From a user perspective, a single tap on a malicious link inside an in-app ' +
  'browser session can result in silent data theft without any visible indication.';

export const mockTechnicalImpact =
  'An attacker who controls a malicious app on the same device, or who can cause the user to click ' +
  'once on a link in the InAppWebView, can trigger a file chooser and then intercept or substitute ' +
  'the activity result with a URI referencing arbitrary files accessible to the host application, ' +
  'such as shared preferences, databases, authentication tokens, or any file within the app data directory.';

export const mockVulnerabilityHowToFix = `
### Disable File Access
Immediately disable file access for the \`WebView\` using:

\`\`\`kotlin
WebViewSettings.setAllowFileAccessFromFileURLs(false);
WebViewSettings.setAllowUniversalAccessFromFileURLs(false);
\`\`\`

### URL Filtering
Implement strict URL filtering to prevent the \`WebView\` from loading unauthorized URLs. This can be done using a whitelist of allowed domains. Specifically, the app should only allow loading URLs from trusted domains.

### Input Validation
Validate all user input, including URLs, to prevent injection of malicious code.

### Content Security Policy (CSP)
If possible, implement a Content Security Policy to restrict the sources from which the \`WebView\` can load resources.

### Regular Updates
Keep the \`WebView\` and the Android operating system up to date with the latest security patches.
`;

export const trialMockCodeBlock = {
  file: 'src/auth/session.js',
  source_type: 'code',
  minSpaces: 0,
  lines: [
    {
      line_number: 1,
      is_matched: false,
      content: '// This is illustrative sample code, not from your actual codebase.',
    },
    {
      line_number: 2,
      is_matched: false,
      content: '// Run a real scan to see your own vulnerabilities here.',
    },
    { line_number: 3, is_matched: false, content: '' },
    { line_number: 4, is_matched: false, content: 'const crypto = require("crypto");' },
    { line_number: 5, is_matched: false, content: '' },
    { line_number: 6, is_matched: false, content: 'function generateToken() {' },
    {
      line_number: 7,
      is_matched: false,
      content: '  return crypto.randomBytes(16).toString("hex");',
    },
    { line_number: 8, is_matched: false, content: '}' },
    { line_number: 9, is_matched: false, content: '' },
    { line_number: 10, is_matched: false, content: 'function createSession(user, res) {' },
    { line_number: 11, is_matched: false, content: '  const token = generateToken();' },
    { line_number: 12, is_matched: false, content: '  const sessionData = {' },
    { line_number: 13, is_matched: false, content: '    userId: user.id,' },
    { line_number: 14, is_matched: false, content: '    role: user.role,' },
    { line_number: 15, is_matched: false, content: '    createdAt: Date.now(),' },
    { line_number: 16, is_matched: false, content: '  };' },
    { line_number: 17, is_matched: false, content: '' },
    {
      line_number: 18,
      is_matched: true,
      content: '  res.cookie("session", token, { httpOnly: false });',
    },
    {
      line_number: 19,
      is_matched: true,
      content: '  res.cookie("user_role", user.role, { secure: false });',
    },
    { line_number: 20, is_matched: false, content: '' },
    { line_number: 21, is_matched: false, content: '  sessions.set(token, sessionData);' },
    { line_number: 22, is_matched: false, content: '  return { token, userId: user.id };' },
    { line_number: 23, is_matched: false, content: '}' },
    { line_number: 24, is_matched: false, content: '' },
    { line_number: 25, is_matched: false, content: 'module.exports = { createSession };' },
  ],
};

export const trialMockProofOfConcepts = [
  {
    kind: 'shell',
    code: [
      {
        source_type: 'shell',
        lines: [
          {
            content:
              'adb shell am start -n poc.app/poc.app.MainActivity --eu uri file:///data/user/0/poc.app/secret.txt',
            is_matched: false,
            line_number: 1,
          },
        ],
      },
    ],
  },
];

export const trialMockStackTraces = [
  {
    threadName: 'main',
    frames: [
      {
        element: {
          fileName: 'File.java',
          className: 'java.io.File',
          lineNumber: 1363,
          methodName: 'mkdirs',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: '58BCB3EE-22D5-447F-BE98-98824A1C3118',
            class_name: 'java.io.File',
          },
        ],
      },
      {
        element: {
          fileName: 'MainActivity.java',
          className: 'poc.app.MainActivity',
          lineNumber: 61,
          methodName: 'onCreate',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: '73F8B88D-7684-4BE2-9E68-36740E93BAE5',
            class_name: 'poc.app.MainActivity',
          },
          {
            name: 'v0',
            is_field: false,
            value_id: '7284540E-68F1-4E30-8E3E-967ADF1EAAA1',
            class_name: 'android.os.Bundle',
          },
        ],
      },
      {
        element: {
          fileName: 'Activity.java',
          className: 'android.app.Activity',
          lineNumber: 9002,
          methodName: 'performCreate',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: '73F8B88D-7684-4BE2-9E68-36740E93BAE5',
            class_name: 'poc.app.MainActivity',
          },
          {
            name: 'icicle',
            is_field: false,
            value_id: 'B91B9D8D-A589-40D2-A9EE-24908273D654',
            class_name: 'android.os.Bundle',
          },
        ],
      },
      {
        element: {
          fileName: 'Instrumentation.java',
          className: 'android.app.Instrumentation',
          lineNumber: 1526,
          methodName: 'callActivityOnCreate',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: 'E693CB7E-0FF0-4D06-BFC8-3947E3D9F464',
            class_name: 'android.app.Instrumentation',
          },
          {
            name: 'activity',
            is_field: false,
            value_id: '73F8B88D-7684-4BE2-9E68-36740E93BAE5',
            class_name: 'android.app.Activity',
          },
        ],
      },
      {
        element: {
          fileName: 'ActivityThread.java',
          className: 'android.app.ActivityThread',
          lineNumber: 4030,
          methodName: 'performLaunchActivity',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: 'FAB871A5-BCA8-4AAE-8A89-B8BB616D5F84',
            class_name: 'android.app.ActivityThread',
          },
        ],
      },
      {
        element: {
          fileName: 'ActivityThread.java',
          className: 'android.app.ActivityThread',
          lineNumber: 4235,
          methodName: 'handleLaunchActivity',
        },
        variables: [
          {
            name: 'this',
            is_field: true,
            value_id: 'FAB871A5-BCA8-4AAE-8A89-B8BB616D5F84',
            class_name: 'android.app.ActivityThread',
          },
        ],
      },
      {
        element: {
          fileName: 'TransactionExecutor.java',
          className: 'android.app.servertransaction.TransactionExecutor',
          lineNumber: 81,
          methodName: 'execute',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'Looper.java',
          className: 'android.os.Looper',
          lineNumber: 232,
          methodName: 'loopOnce',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'Looper.java',
          className: 'android.os.Looper',
          lineNumber: 317,
          methodName: 'loop',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'ActivityThread.java',
          className: 'android.app.ActivityThread',
          lineNumber: 8705,
          methodName: 'main',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'Method.java',
          className: 'java.lang.reflect.Method',
          lineNumber: -1,
          methodName: 'invoke',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'RuntimeInit.java',
          className: 'com.android.internal.os.RuntimeInit$MethodAndArgsCaller',
          lineNumber: 580,
          methodName: 'run',
        },
        variables: [],
      },
      {
        element: {
          fileName: 'ZygoteInit.java',
          className: 'com.android.internal.os.ZygoteInit',
          lineNumber: 886,
          methodName: 'main',
        },
        variables: [],
      },
    ],
    values: [
      {
        id: '58BCB3EE-22D5-447F-BE98-98824A1C3118',
        class_name: 'java.io.File',
        value_type: 'string',
        value_content: {
          display_value: '/storage/emulated/0/Android/data/poc.app/cache',
          identity_hash_code: 727415262,
        },
      },
      {
        id: '73F8B88D-7684-4BE2-9E68-36740E93BAE5',
        class_name: 'poc.app.MainActivity',
        value_type: 'hidden',
        value_content: { display_value: 'HIDDEN', identity_hash_code: 1325261268 },
      },
      {
        id: '7284540E-68F1-4E30-8E3E-967ADF1EAAA1',
        class_name: 'android.net.Uri$StringUri',
        value_type: 'string',
        value_content: {
          display_value: 'file:///data/user/0/poc.app/secret.txt',
          identity_hash_code: 1648328188,
        },
      },
      {
        id: 'E693CB7E-0FF0-4D06-BFC8-3947E3D9F464',
        class_name: 'android.app.Instrumentation',
        value_type: 'hidden',
        value_content: { display_value: 'HIDDEN', identity_hash_code: 597875991 },
      },
      {
        id: 'FAB871A5-BCA8-4AAE-8A89-B8BB616D5F84',
        class_name: 'android.app.ActivityThread',
        value_type: 'hidden',
        value_content: { display_value: 'HIDDEN', identity_hash_code: 327460136 },
      },
    ],
    highlights: [
      {
        path: [{ name: 'this', type: 'field' }],
        frame_index: 1,
      },
    ],
  },
];

export const trialMockAttachments = [
  {
    attachment_path: 'attachments/63EBC6E1-F10B-490A-9360-4F85CB75B01E.mp4',
    attachment_type: 'video' as const,
  },
];
